import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaUsers, FaVideo, FaMapMarkerAlt, FaCalendarPlus, FaCheck, FaTimes, FaLightbulb } from "react-icons/fa";
import { AppCard, AppButton, Badge, IconBox } from "../app";
import { appColors, appType, appRadius } from "../../utils/appTheme";
import {
  asambleaPropietario,
  descargarIcs,
  detallesDeError,
  entrar,
  enVentana,
  horaMontevideo,
  inicioDiaAnteriorMs,
  urlNavegable,
  ventanaEntrar,
} from "../../utils/asambleas/api";
import { useAhoraEnLimites } from "../../utils/asambleas/reloj";
import { enlaceGoogleCalendar, enlaceOutlook, textoDelEvento } from "../../utils/asambleas/calendario";
import { fechaHoraAsamblea, normalizarIdioma } from "../../utils/asambleas/idioma";

/**
 * Tarjeta de una asamblea para el propietario: fecha, lugar, orden del día,
 * confirmación de asistencia y "agregar al calendario". La usan la pantalla
 * Asambleas y el Inicio (próxima asamblea).
 *
 * Con videollamada (video.proveedor "zoom" o "manual", remota o mixta, con
 * citación): botón "Entrar" de T-15 al cierre de la sala (callable entrar →
 * abre su acceso personal de Zoom o el link que pegó la administración);
 * antes, a qué hora se habilita; con Zoom, desde el día anterior, el consejo
 * de instalar Zoom y probar el audio. La tarjeta se actualiza sola en esos
 * bordes, sin consultar al servidor. El acceso no se guarda en ningún lado.
 */
const TONO = { convocada: "blue", en_curso: "amber", finalizada: "ink3", cancelada: "red" };

const ESTADOS_CON_ENTRADA = new Set(["convocada", "en_curso"]);
const PROVEEDORES_CON_ENTRADA = new Set(["zoom", "manual"]);

/** Texto del error de "entrar" según el motivo que mandó el servidor. */
function mensajeEntrar(t, e) {
  const detalles = detallesDeError(e);
  // Reloj del teléfono adelantado: el servidor dice desde qué hora se puede entrar.
  const habilitaEnMs = Number(detalles.habilitaEnMs);
  if (Number.isFinite(habilitaEnMs) && habilitaEnMs > 0) return t("entrar.habilitaA", { hora: horaMontevideo(habilitaEnMs) });
  if (detalles.motivo === "sin_video") return t("entrar.sinLink");
  if (detalles.motivo === "sin_acceso") return t("entrar.sinAcceso");
  if (detalles.motivo === "terminada") return t("entrar.terminada");
  // Se suspendió después de que se cargó la tarjeta.
  if (detalles.motivo === "cancelada") return t("entrar.cancelada");
  // Módulo apagado (el rollback): no sale ningún acceso hasta que Habitia lo vuelva a prender.
  if (detalles.motivo === "no_disponible") return t("entrar.noDisponible");
  return t("entrar.error");
}

const enlaceComoBoton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "11px 14px",
  borderRadius: appRadius.button,
  border: `1px solid ${appColors.line}`,
  color: appColors.ink,
  background: "transparent",
  fontFamily: appType.fontFamily,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1,
  textDecoration: "none",
  transition: "background .15s ease, border-color .15s ease",
};

// La fecha va sin tabular-nums: en FT Habit esa feature cambia "," y ":" por
// glifos de ancho fijo (600 u. contra ~240) y la fecha se ve con espacios de más.
const estiloFecha = { ...appType.stat, color: appColors.ink, fontVariantNumeric: "normal" };

const horaSegunda = (a) => horaMontevideo(Number(a.inicioMs) + Number(a.minutosSegunda || 60) * 60000);

export default function AsambleaCard({ a, onCambio }) {
  // Dentro de la app: el idioma del propietario (users.idioma, LanguageProvider).
  const { t, i18n } = useTranslation("asambleas");
  const idioma = normalizarIdioma(i18n.language);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [editando, setEditando] = useState(false);
  // Acceso a la videollamada: solo en memoria, para los botones de respaldo.
  const [acceso, setAcceso] = useState(null);
  const [entrando, setEntrando] = useState(false);
  const [errorEntrar, setErrorEntrar] = useState("");
  const rsvp = a.miInvitacion ? a.miInvitacion.rsvp : null;
  const remota = a.modalidad === "remota";
  const texto = textoDelEvento(a, a.edificio ? a.edificio.nombre : "", t);

  // Videollamada (Zoom automático o link manual): ventana de "Entrar" (T-15 al
  // cierre de la sala) y, solo con Zoom, el consejo desde el día anterior.
  const proveedor = a.video ? a.video.proveedor : null;
  const conVideo =
    PROVEEDORES_CON_ENTRADA.has(proveedor) && a.modalidad !== "presencial" && ESTADOS_CON_ENTRADA.has(a.estado) && !!a.miInvitacion;
  const conZoom = conVideo && proveedor === "zoom";
  const ventana = conVideo ? ventanaEntrar(a) : null;
  const diaAnteriorMs = conZoom ? inicioDiaAnteriorMs(a) : null;
  const ahora = useAhoraEnLimites(ventana ? [diaAnteriorMs, Number(a.inicioMs), ventana.desdeMs, ventana.hastaMs] : [Number(a.inicioMs)]);
  const puedeEntrar = conVideo && enVentana(ventana, ahora);
  const antesDeEntrar = conVideo && !!ventana && ahora < ventana.desdeMs;
  // diaAnteriorMs solo existe con Zoom: el consejo de instalar Zoom no va con un link manual.
  const conConsejo = diaAnteriorMs != null && !!ventana && ahora >= diaAnteriorMs && ahora < ventana.hastaMs;
  const abierta = a.estado === "convocada" && Number(a.inicioMs) > ahora;

  const entrarAhora = async () => {
    setErrorEntrar("");
    // La pestaña se abre en el mismo toque (antes del await) para que el navegador
    // no la bloquee; el acceso se carga ahí cuando llega.
    let pestana = null;
    try {
      pestana = window.open("", "_blank");
    } catch (_) {
      pestana = null;
    }
    if (pestana) {
      try {
        pestana.opener = null;
      } catch (_) {
        /* algunos navegadores no lo dejan cambiar */
      }
    }
    setEntrando(true);
    try {
      const res = await entrar(a.id);
      const joinUrl = urlNavegable(res && res.joinUrl);
      if (!joinUrl) throw new Error("sin acceso");
      setAcceso({
        joinUrl,
        webUrl: urlNavegable(res && res.webUrl),
        proveedor: (res && PROVEEDORES_CON_ENTRADA.has(res.proveedor) && res.proveedor) || proveedor,
      });
      if (pestana && !pestana.closed) pestana.location.href = joinUrl;
    } catch (e) {
      if (pestana && !pestana.closed) {
        try {
          pestana.close();
        } catch (_) {
          /* ya cerrada */
        }
      }
      setErrorEntrar(mensajeEntrar(t, e));
    } finally {
      setEntrando(false);
    }
  };

  const responder = async (respuesta) => {
    setGuardando(true);
    setError("");
    try {
      await asambleaPropietario("rsvp", { aid: a.id, respuesta, version: a.version });
      setEditando(false);
      await onCambio();
    } catch (e) {
      setError(t("asistencia.error"));
    } finally {
      setGuardando(false);
    }
  };

  const bajarIcs = async () => {
    try {
      const res = await asambleaPropietario("ics", { aid: a.id });
      descargarIcs(res.ics, res.nombreArchivo);
    } catch (e) {
      setError(t("asistencia.error"));
    }
  };

  const etiqueta = { ...appType.statLabel, color: appColors.ink3, marginBottom: 3 };

  return (
    <AppCard accent={a.estado === "cancelada" ? undefined : "blue"} padding={0} style={{ overflow: "hidden" }}>
      <div style={{ padding: "16px 16px 0", display: "flex", alignItems: "flex-start", gap: 12 }}>
        <IconBox icon={remota ? <FaVideo /> : <FaUsers />} color={appColors.blue} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...appType.statLabel, color: appColors.blue, marginBottom: 3 }}>{t(`estado.${a.estado}`)}</div>
          <div style={{ ...appType.cardTitle, fontSize: 16, overflowWrap: "anywhere" }}>{t(`tipo.${a.tipo}`)}</div>
        </div>
        <Badge tone={TONO[a.estado] || "blue"} dot>{t(`modalidad.${a.modalidad}`)}</Badge>
      </div>

      <div style={{ padding: "12px 16px 0" }}>
        <div style={{ background: appColors.cream, borderRadius: appRadius.tile, padding: "12px 14px" }}>
          <div style={etiqueta}>{t("fechaHora")}</div>
          <div style={estiloFecha}>{fechaHoraAsamblea(a.inicioMs, idioma)}</div>
          <div style={{ ...appType.body, fontSize: 13, color: appColors.ink3, marginTop: 4 }}>{t("segunda", { hora: horaSegunda(a) })}</div>
        </div>
      </div>

      <div style={{ padding: "12px 16px 0", display: "flex", flexDirection: "column", gap: 10 }}>
        {!remota && a.lugar && (
          <div>
            <div style={etiqueta}>{t("lugar")}</div>
            <div style={{ ...appType.body, display: "flex", alignItems: "center", gap: 6 }}>
              <FaMapMarkerAlt style={{ color: appColors.blue, fontSize: 12 }} /> {a.lugar}
            </div>
          </div>
        )}
        <div>
          <div style={etiqueta}>{t("ordenDelDia")}</div>
          <ol style={{ margin: 0, paddingLeft: 18, ...appType.body }}>
            {(a.ordenDelDia || []).map((p) => (
              <li key={p.id} style={{ marginBottom: 4 }}>
                {p.titulo}
                {p.detalle && <div style={{ fontSize: 13, color: appColors.ink3 }}>{p.detalle}</div>}
              </li>
            ))}
          </ol>
        </div>
        {a.miInvitacion && a.miInvitacion.unidades && a.miInvitacion.unidades.length > 0 && (
          <div>
            <div style={etiqueta}>{t("tusUnidades")}</div>
            <div style={appType.body}>{a.miInvitacion.unidades.map((u) => u.etiqueta).join(", ")}</div>
          </div>
        )}
        {a.estado === "cancelada" && a.motivoCancelacion && (
          <div style={{ ...appType.body, color: appColors.red }}>{t("motivoCancelacion", { motivo: a.motivoCancelacion })}</div>
        )}
      </div>

      {(puedeEntrar || antesDeEntrar || conConsejo) && (
        <div style={{ padding: "12px 16px 0", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("entrar.titulo")}</div>
          {puedeEntrar && (
            <AppButton variant="primary" block icon={<FaVideo size={13} />} disabled={entrando} onClick={entrarAhora}>
              {entrando ? t("entrar.abriendo") : t("entrar.boton")}
            </AppButton>
          )}
          {antesDeEntrar && (
            <div style={{ ...appType.body, color: appColors.ink2 }}>{t("entrar.habilitaA", { hora: horaMontevideo(ventana.desdeMs) })}</div>
          )}
          {puedeEntrar && acceso && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ ...appType.body, fontSize: 13, color: appColors.ink3 }}>
                {acceso.webUrl ? t("entrar.noAbrio") : t("entrar.noAbrioUno")}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {acceso.webUrl ? (
                  <>
                    <a href={acceso.joinUrl} target="_blank" rel="noopener noreferrer" style={enlaceComoBoton}>
                      {t("entrar.app")}
                    </a>
                    <a href={acceso.webUrl} target="_blank" rel="noopener noreferrer" style={enlaceComoBoton}>
                      {t("entrar.navegador")}
                    </a>
                  </>
                ) : (
                  // Link manual (Zoom, Meet, Teams…) o Zoom sin versión navegador: un solo botón.
                  <a href={acceso.joinUrl} target="_blank" rel="noopener noreferrer" style={enlaceComoBoton}>
                    {t("entrar.abrir")}
                  </a>
                )}
              </div>
              <div style={{ ...appType.body, fontSize: 12, color: appColors.ink3 }}>
                {acceso.proveedor === "manual" ? t("entrar.compartido") : t("entrar.personal")}
              </div>
            </div>
          )}
          {conConsejo && (
            <div style={{ ...appType.body, fontSize: 13, color: appColors.ink2, display: "flex", alignItems: "flex-start", gap: 8 }}>
              <FaLightbulb style={{ color: appColors.amber, fontSize: 13, marginTop: 2, flexShrink: 0 }} aria-hidden="true" />
              <span>{t("entrar.consejo")}</span>
            </div>
          )}
          {errorEntrar && (
            <div role="alert" style={{ ...appType.body, color: appColors.red }}>
              {errorEntrar}
            </div>
          )}
        </div>
      )}

      {abierta && a.miInvitacion && (
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {rsvp && !editando ? (
            <>
              <div style={{ ...appType.body, display: "flex", alignItems: "center", gap: 8, color: rsvp === "si" ? appColors.green : appColors.ink3 }}>
                {rsvp === "si" ? <FaCheck /> : <FaTimes />} {rsvp === "si" ? t("asistencia.confirmadoSi") : t("asistencia.confirmadoNo")}
              </div>
              {rsvp === "si" && (
                <div>
                  <div style={etiqueta}>{t("calendario.label")}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <AppButton variant="outline" icon={<FaCalendarPlus size={13} />}
                      onClick={() => window.open(enlaceGoogleCalendar(a, texto), "_blank", "noopener,noreferrer")}>
                      {t("calendario.google")}
                    </AppButton>
                    <AppButton variant="outline" icon={<FaCalendarPlus size={13} />}
                      onClick={() => window.open(enlaceOutlook(a, texto), "_blank", "noopener,noreferrer")}>
                      {t("calendario.outlook")}
                    </AppButton>
                    <AppButton variant="outline" icon={<FaCalendarPlus size={13} />} onClick={bajarIcs}>
                      {t("calendario.ics")}
                    </AppButton>
                  </div>
                </div>
              )}
              <AppButton variant="ghost" onClick={() => setEditando(true)}>{t("asistencia.cambiar")}</AppButton>
            </>
          ) : (
            <>
              <div style={etiqueta}>{t("asistencia.label")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <AppButton variant="primary" block disabled={guardando} onClick={() => responder("si")}>
                  {guardando ? t("asistencia.guardando") : t("asistencia.si")}
                </AppButton>
                <AppButton variant="outline" block disabled={guardando} onClick={() => responder("no")}>
                  {t("asistencia.no")}
                </AppButton>
              </div>
            </>
          )}
          {error && <div role="alert" style={{ ...appType.body, color: appColors.red }}>{error}</div>}
        </div>
      )}
      {!abierta && <div style={{ height: 16 }} />}
    </AppCard>
  );
}
